import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  UserPlus,
  Send,
  Clock,
  CheckCircle2,
  XCircle,
  MessageSquare,
  Loader2,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  MapPin,
  AlertCircle
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

// Component for Titolare to create requests
export const CollaboratorRequestForm = ({ onRequestSent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome_proposto: "",
    cognome_proposto: "",
    email_proposto: "",
    telefono_proposto: "",
    motivo: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome_proposto || !formData.motivo) {
      toast.error("Nome e motivo sono obbligatori");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/collaborator-requests`, formData, {
        withCredentials: true
      });
      toast.success("Segnalazione inviata all'Admin!");
      setFormData({
        nome_proposto: "",
        cognome_proposto: "",
        email_proposto: "",
        telefono_proposto: "",
        motivo: ""
      });
      setIsOpen(false);
      if (onRequestSent) onRequestSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio della segnalazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
        data-testid="collaborator-request-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white">
            <UserPlus className="w-5 h-5" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-slate-900 dark:text-white">Richiedi Nuovo Collaboratore</h3>
            <p className="text-sm text-slate-500">Invia una segnalazione all'Admin</p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {isOpen && (
        <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200 dark:border-slate-700 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="nome_proposto">Nome *</Label>
              <Input
                id="nome_proposto"
                name="nome_proposto"
                value={formData.nome_proposto}
                onChange={handleChange}
                placeholder="Nome del candidato"
                className="mt-1"
                data-testid="request-nome"
              />
            </div>
            <div>
              <Label htmlFor="cognome_proposto">Cognome</Label>
              <Input
                id="cognome_proposto"
                name="cognome_proposto"
                value={formData.cognome_proposto}
                onChange={handleChange}
                placeholder="Cognome"
                className="mt-1"
                data-testid="request-cognome"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email_proposto" className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                Email
              </Label>
              <Input
                id="email_proposto"
                name="email_proposto"
                type="email"
                value={formData.email_proposto}
                onChange={handleChange}
                placeholder="email@esempio.it"
                className="mt-1"
                data-testid="request-email"
              />
            </div>
            <div>
              <Label htmlFor="telefono_proposto" className="flex items-center gap-1">
                <Phone className="w-3 h-3" />
                Telefono
              </Label>
              <Input
                id="telefono_proposto"
                name="telefono_proposto"
                value={formData.telefono_proposto}
                onChange={handleChange}
                placeholder="333 1234567"
                className="mt-1"
                data-testid="request-telefono"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="motivo" className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              Motivo della richiesta *
            </Label>
            <Textarea
              id="motivo"
              name="motivo"
              value={formData.motivo}
              onChange={handleChange}
              placeholder="Spiega perché hai bisogno di questo collaboratore..."
              className="mt-1 min-h-24"
              data-testid="request-motivo"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700"
            data-testid="request-submit"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Invia Segnalazione
          </Button>
        </form>
      )}
    </div>
  );
};

// Component for viewing requests (for both Titolare and Admin)
export const CollaboratorRequestsList = ({ isAdmin = false, onUpdate }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/collaborator-requests`, {
        withCredentials: true
      });
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAction = async (requestId, status) => {
    setActionLoading(requestId);
    try {
      await axios.put(
        `${API}/collaborator-requests/${requestId}?status=${status}${adminNotes ? `&notes=${encodeURIComponent(adminNotes)}` : ''}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Richiesta ${status === 'approved' ? 'approvata' : 'rifiutata'}`);
      setAdminNotes("");
      setExpandedId(null);
      fetchRequests();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiornamento");
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <Clock className="w-3 h-3" />
            In attesa
          </span>
        );
      case "approved":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle2 className="w-3 h-3" />
            Approvata
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <XCircle className="w-3 h-3" />
            Rifiutata
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500">Nessuna segnalazione trovata</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {requests.map((request) => (
        <div
          key={request.request_id}
          className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        >
          <div
            className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
            onClick={() => setExpandedId(expandedId === request.request_id ? null : request.request_id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center text-white font-medium">
                  {request.nome_proposto?.charAt(0)?.toUpperCase()}
                  {request.cognome_proposto?.charAt(0)?.toUpperCase() || ""}
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {request.nome_proposto} {request.cognome_proposto || ""}
                  </p>
                  <p className="text-xs text-slate-500">
                    {isAdmin && <span>Da: {request.titolare_name} • </span>}
                    {request.macro_area} • {new Date(request.created_at).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(request.status)}
                {expandedId === request.request_id ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </div>
            </div>
          </div>

          {expandedId === request.request_id && (
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {request.email_proposto && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Mail className="w-4 h-4" />
                    {request.email_proposto}
                  </div>
                )}
                {request.telefono_proposto && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                    <Phone className="w-4 h-4" />
                    {request.telefono_proposto}
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                  <MapPin className="w-4 h-4" />
                  Area: {request.macro_area}
                </div>
              </div>

              <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <p className="text-xs font-medium text-slate-500 mb-1">Motivo:</p>
                <p className="text-sm text-slate-700 dark:text-slate-300">{request.motivo}</p>
              </div>

              {request.admin_notes && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Note Admin:</p>
                  <p className="text-sm text-blue-700 dark:text-blue-300">{request.admin_notes}</p>
                </div>
              )}

              {isAdmin && request.status === "pending" && (
                <div className="space-y-3 pt-2">
                  <div>
                    <Label htmlFor={`notes-${request.request_id}`}>Note (opzionale)</Label>
                    <Textarea
                      id={`notes-${request.request_id}`}
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Aggiungi note per il Titolare..."
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAction(request.request_id, "approved")}
                      disabled={actionLoading === request.request_id}
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      data-testid={`approve-${request.request_id}`}
                    >
                      {actionLoading === request.request_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Approva
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => handleAction(request.request_id, "rejected")}
                      disabled={actionLoading === request.request_id}
                      variant="destructive"
                      className="flex-1"
                      data-testid={`reject-${request.request_id}`}
                    >
                      {actionLoading === request.request_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-1" />
                          Rifiuta
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default { CollaboratorRequestForm, CollaboratorRequestsList };
